import { Plug, Puzzle, Unplug } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { api, type ConnectedApp } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import ClaudeLogo from "../../components/ClaudeLogo.tsx";
import EmptyState from "../../components/EmptyState.tsx";
import FormattedDate from "../../components/FormattedDate.tsx";
import IdentityCell from "../../components/IdentityCell.tsx";
import LogoBadge from "../../components/LogoBadge.tsx";
import RelativeTime from "../../components/RelativeTime.tsx";
import RowActions from "../../components/RowActions.tsx";
import SectionHeading from "../../components/SectionHeading.tsx";
import ScrollTable from "../../components/table/ScrollTable.tsx";
import TableCard from "../../components/table/TableCard.tsx";

function AppLogo({ app }: { app: ConnectedApp }) {
  return (
    <LogoBadge size={32}>
      {/claude/i.test(app.name) ? (
        <ClaudeLogo sx={{ fontSize: 18 }} />
      ) : (
        <Puzzle sx={{ fontSize: 18, color: "text.secondary" }} />
      )}
    </LogoBadge>
  );
}

/** Apps (e.g. Claude) you've let into your account through Vayken MCP. */
export default function ConnectedAppsPage() {
  const queryClient = useQueryClient();
  const apps = useQuery({ queryKey: keys.account.connectedApps(), queryFn: api.getConnectedApps });
  const revoke = useMutation({
    mutationFn: api.revokeConnectedApp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.account.connectedApps() });
      toast.success("Access revoked");
    },
  });

  const rows = apps.data ?? [];
  const connect = (
    <Button component={Link} to="/connect/mcp" size="small" variant="outlined" startIcon={<Plug />}>
      Connect Vayken MCP
    </Button>
  );

  if (apps.isSuccess && rows.length === 0) {
    return (
      <EmptyState
        variant="panel"
        icon={<Plug />}
        title="No connected apps yet"
        description="Connect Claude or another MCP client to Vayken, and it shows up here with what it can do. You can take its access back at any time."
        action={connect}
      />
    );
  }

  return (
    <Box>
      <SectionHeading title="Apps with access" count={apps.data ? rows.length : undefined} action={connect} />
      <TableCard loading={apps.isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>App</TableCell>
              <TableCell>Can</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Connected</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Last used</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((app) => (
              <TableRow key={app.client_id}>
                <TableCell>
                  <IdentityCell avatar={<AppLogo app={app} />} name={app.name} />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {app.scopes.map((s) => (
                      <Chip key={s.id} size="small" variant="outlined" label={s.label} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <FormattedDate value={app.authorized_at} />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <RelativeTime value={app.last_used_at} />
                </TableCell>
                <TableCell padding="checkbox" align="right">
                  <RowActions
                    label={app.name}
                    actions={[
                      {
                        label: "Revoke access",
                        icon: <Unplug />,
                        destructive: true,
                        onClick: () => revoke.mutateAsync(app.client_id),
                        confirm: {
                          title: `Revoke access: ${app.name}`,
                          body: "It's signed out of your account and has to ask for access again.",
                          confirmLabel: "Revoke",
                        },
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </Box>
  );
}
